import { useEffect, useState } from 'react'

import { useLazyGetComputerCountDataQuery } from '../../app/apiSliceCoreLogicApi'
import useBusinessAppsFilters from '../../features/BusinessApps/useBusinessAppsFilters'
import { getWeekdayDateRanges } from '../../features/BusinessApps/utils'
import useQueryFilters from './useQueryFilters'

interface UseComputerCountQueryResult {
  dailyComputerCountSum: number | undefined
  isLoading: boolean
  error: string | null
}

export const useComputerCountQuery = (): UseComputerCountQueryResult => {
  const { min_date, max_date, min_date_iso, max_date_iso, team_ids } = useQueryFilters()
  const { isIgnoreWeekendsActive } = useBusinessAppsFilters()

  const [dailyComputerCountSum, setDailyComputerCountSum] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [getComputerCountData] = useLazyGetComputerCountDataQuery()

  useEffect(() => {
    const fetchComputerCountData = async () => {
      if (!min_date || !max_date) return

      setIsLoading(true)
      setError(null)

      try {
        let computerCountSum: number | undefined

        if (!isIgnoreWeekendsActive) {
          // Regular fetch for all days including weekends
          const computerCountResult = await getComputerCountData({
            team_ids,
            start_timestamp: min_date_iso,
            end_timestamp: max_date_iso,
          })
          computerCountSum = computerCountResult.data
        } else {
          // Fetch for weekdays only
          const dateRanges = getWeekdayDateRanges(min_date, max_date)

          // Get computer count data for all weekday ranges
          const computerCountPromises = dateRanges.map(({ firstDateIso, lastDateIso }) =>
            getComputerCountData({
              team_ids,
              start_timestamp: firstDateIso,
              end_timestamp: lastDateIso,
            }),
          )

          const computerCountResults = await Promise.all(computerCountPromises)

          // Calculate sum for dailyComputerCountSum
          computerCountSum = computerCountResults
            .map((result) => result.data as number | undefined)
            .filter((count): count is number => typeof count === 'number')
            .reduce((sum, count) => sum + count, 0)
        }

        setDailyComputerCountSum(computerCountSum)
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching computer count data'
        setError(errorMessage)
        console.error('Error fetching computer count data:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchComputerCountData()
  }, [
    min_date,
    max_date,
    min_date_iso,
    max_date_iso,
    team_ids,
    isIgnoreWeekendsActive,
    getComputerCountData,
  ])

  return {
    dailyComputerCountSum,
    isLoading,
    error,
  }
}

